/**
 * Chat-Pool für die „Vorher: Chat-Chaos"-Mockup-Darstellung.
 * Bei jedem Seitenaufruf wird ein zufälliger, zusammenhängender Ausschnitt
 * (Fenster-Länge z. B. 47) aus dem Pool gezeigt — so sieht der Besucher
 * immer wieder was Neues, inkl. Gags.
 *
 * Messages sind bewusst mockup-artig und nicht i18n-integriert (das wären
 * ~250 Keys pro Sprache, was die Locale-Dateien unnötig aufblähen würde).
 */

export type ChatMsg = {
  person: string;
  text: string;
  side: "left" | "right";
};

/** 10 Personen — die Initialen/Avatare kommen aus lib/avatar. */
const P = {
  A: "Anna",
  B: "Ben",
  C: "Clara",
  D: "Dani",
  E: "Emma",
  F: "Finn",
  G: "Gabi",
  H: "Hans",
  I: "Ida",
  J: "Jonas",
  K: "Klara",
  L: "Lukas",
  M: "Mama",
  N: "Niko",
  O: "Olli",
  P: "Pia",
  Q: "Quentin",
  R: "Resi",
  S: "Sven",
  T: "Tina",
};

function mk(
  items: Array<[keyof typeof P, string, "left" | "right"]>,
): ChatMsg[] {
  return items.map(([k, text, side]) => ({ person: P[k], text, side }));
}

const DE_MSGS: ChatMsg[] = mk([
  ["A", "Hey zusammen! Wer bringt was mit?", "left"],
  ["B", "Ich kann Salat machen 🥗", "right"],
  ["C", "Salat klingt gut!", "left"],
  ["D", "Ich auch Salat? Oder lieber was anderes?", "right"],
  ["B", "Mach du Kartoffelsalat, ich Nudel", "left"],
  ["E", "Kuchen von mir", "right"],
  ["F", "Ich bring die Getränke", "left"],
  ["G", "Welche Getränke?", "right"],
  ["F", "Bier, Wein, Wasser?", "left"],
  ["G", "Alkoholfrei für die Kinder bitte auch", "right"],
  ["F", "Ach stimmt, die Kids", "left"],
  ["H", "Brot bring ich", "right"],
  ["I", "Ich hab ne Grillzange zuhause, nehm ich mit", "left"],
  ["J", "Nimmt jemand Teller mit?", "right"],
  ["A", "Oh, Teller? Hab ich nicht dran gedacht", "left"],
  ["K", "Ich hab noch Pappteller vom letzten Mal", "right"],
  ["L", "Kommt jemand mit Auto?", "left"],
  ["M", "Ich nehm die Kinder mit", "right"],
  ["N", "Cool, dann komm ich auch", "left"],
  ["O", "Wie lang geht das ungefähr?", "right"],
  ["A", "Bis wir fertig sind 😄", "left"],
  ["B", "Haha typisch Anna", "right"],
  ["P", "Bin 10 Minuten später, geht das?", "left"],
  ["A", "Klar, lass dir Zeit", "right"],
  ["Q", "Gibt's WLAN? Ich muss vielleicht arbeiten", "left"],
  ["A", "Quentin wir feiern, kein Homeoffice", "right"],
  ["R", "Hahahaha", "left"],
  ["S", "Bin dabei, bring Musikbox mit", "right"],
  ["T", "Nein, keine Musikbox. Bitte.", "left"],
  ["S", "Doch.", "right"],
  ["T", "Nein.", "left"],
  ["S", "Kompromiss: leise Musikbox", "right"],
  ["T", "Okay", "left"],
  ["A", "Leute, was ist jetzt mit Salat??", "right"],
  ["B", "Ich Nudel, C Kartoffel?", "left"],
  ["C", "Ich dachte ich bring Kuchen", "right"],
  ["E", "Ich dachte ICH bring Kuchen!", "left"],
  ["A", "Also zwei Kuchen?", "right"],
  ["C", "Okay ich mach Kartoffelsalat", "left"],
  ["B", "Dann mach ich Nudel", "right"],
  ["A", "Aber jemand sagte schon Nudel?", "left"],
  ["B", "Das war ich. Immer noch.", "right"],
  ["A", "Sorry hab den Überblick verloren", "left"],
  ["D", "Ich bring Obst", "right"],
  ["F", "Obst ist gut!", "left"],
  ["G", "Welche Sorte?", "right"],
  ["D", "Weiß noch nicht, mal schauen was der Markt hat", "left"],
  ["H", "Brot ist abgesichert, ich bring 3 Brote", "right"],
  ["A", "3 Brote für 15 Leute?", "left"],
  ["H", "Besser zuviel als zuwenig", "right"],
  ["I", "Brauch ich was mitbringen oder ist alles abgesprochen?", "left"],
  ["A", "Moment, ich muss eine Liste machen 📝", "right"],
  ["B", "Du machst immer Listen", "left"],
  ["A", "Weil's sonst eskaliert wie jetzt", "right"],
  ["J", "Allergien? Hat jemand Nussallergie?", "left"],
  ["K", "Ich bin Gluten-frei, aber bring was für mich mit", "right"],
  ["L", "Ich vegan", "left"],
  ["M", "Meine Kids mögen kein Gemüse 🙄", "right"],
  ["N", "Ich esse alles 👍", "left"],
  ["O", "Wo ist das nochmal?", "right"],
  ["A", "Bei mir im Garten, Gartenstraße 12", "left"],
  ["O", "Google Maps Link?", "right"],
  ["A", "Such selber 😏", "left"],
  ["P", "Was ziehen wir an?", "right"],
  ["T", "Ganz normal", "left"],
  ["S", "Smoking?", "right"],
  ["T", "Sven nein", "left"],
  ["A", "Wer hat noch nicht geantwortet?", "right"],
  ["B", "Gabi, Hans, Ida…", "left"],
  ["G", "Hier, Gabi ist da", "right"],
  ["H", "Ich hab doch Brot zugesagt?", "left"],
  ["I", "Sorry war bei Oma, was soll ich bringen?", "right"],
  ["A", "Hmm… Chips? Salzstangen?", "left"],
  ["I", "Kann ich machen", "right"],
  ["J", "Ich krieg grad 200 Nachrichten, was ist los?", "left"],
  ["A", "Wir planen die Party 😅", "right"],
  ["J", "Ach ja", "left"],
  ["C", "Kartoffelsalat mit oder ohne Mayo?", "right"],
  ["A", "Beides? Machen viele Mayo, viele nicht", "left"],
  ["C", "Okay mach ich ohne, wer Mayo will macht selber drauf", "right"],
  ["B", "Gute Idee", "left"],
  ["D", "Grillgut? Wer bringt Fleisch?", "right"],
  ["A", "STIMMT. Fleisch!", "left"],
  ["F", "Ich kann Bratwürste organisieren", "right"],
  ["G", "Veggie-Würstchen auch?", "left"],
  ["F", "Kann ich vom Metzger mitnehmen", "right"],
  ["L", "Ich bring mein eigenes Tofu-Zeug", "left"],
  ["M", "Tofu 🤢", "right"],
  ["L", "Mama nicht meckern", "left"],
  ["M", "Ich bin nicht deine Mama", "right"],
  ["L", "Stimmt, aber Mama klingt hier nach Mama", "left"],
  ["A", "Leute fokussiert", "right"],
  ["N", "Hat jemand einen Schirm falls es regnet?", "left"],
  ["A", "Pavillon steht schon im Garten", "right"],
  ["N", "Topp", "left"],
  ["O", "Ab wann genau?", "right"],
  ["A", "17 Uhr hab ich gesagt", "left"],
  ["O", "War das nicht 18 Uhr?", "right"],
  ["A", "17. Steht in der ersten Nachricht oben.", "left"],
  ["O", "Oben wo? Hier scrollen?", "right"],
  ["A", "🙄", "left"],
  ["P", "Ich bin Vegetarier, kein Fleisch für mich bitte", "right"],
  ["F", "Notiert. Veggie-Wurst kommt.", "left"],
  ["Q", "Darf ich meinen Kollegen mitbringen? Er ist neu hier", "right"],
  ["A", "Klar, gerne", "left"],
  ["Q", "Cool er bringt seine Freundin mit, ok?", "right"],
  ["A", "Noch cooler, willkommen 👋", "left"],
  ["Q", "Sie ist schwanger, darf ich ihren Bruder auch mitbringen?", "right"],
  ["A", "Äh, ja? Wie viele werden wir jetzt?", "left"],
  ["Q", "Weiß nicht genau", "right"],
  ["A", "Quentin 😑", "left"],
  ["R", "Kommen eure Hunde auch?", "right"],
  ["A", "Ja, meine sind eh da", "left"],
  ["R", "Mein Balu ist aber manchmal zickig", "right"],
  ["A", "Lass ihn zuhause dann lieber", "left"],
  ["R", "Dann komm ich auch nicht 😢", "right"],
  ["A", "Doch bitte! Dann nimm Balu mit, wird schon", "left"],
  ["S", "Ich bring Kühltasche und Eis", "right"],
  ["T", "Gute Idee", "left"],
  ["S", "Und die Musikbox.", "right"],
  ["T", "Sven nein.", "left"],
  ["S", "T.", "right"],
  ["T", "S.", "left"],
  ["A", "Ihr zwei seid anstrengend 😂", "right"],
  ["B", "Wer bringt Servietten?", "left"],
  ["A", "Hab ich", "right"],
  ["C", "Ketchup & Senf?", "left"],
  ["F", "Nehm ich beim Metzger mit", "right"],
  ["D", "Obst-Update: Erdbeeren + Melone", "left"],
  ["A", "Mmmh", "right"],
  ["E", "Kuchen-Update: Apfelkuchen + Käsekuchen", "left"],
  ["C", "Kuchen fällt wegen Kartoffelsalat weg? Oder beides?", "right"],
  ["A", "E macht Kuchen, du Kartoffelsalat", "left"],
  ["E", "Dann back ich nur Apfel", "right"],
  ["A", "Okay Apfel 👍", "left"],
  ["G", "Ich bring Apfel-Tarte", "right"],
  ["A", "WAS", "left"],
  ["G", "Apfel-Tarte ist was anderes als Apfelkuchen", "right"],
  ["A", "Okay, dann haben wir beide", "left"],
  ["H", "Zu viel Apfel?", "right"],
  ["A", "Kann man nie zu viel haben", "left"],
  ["I", "Ich bring noch Käse-Platte", "right"],
  ["A", "Wunderbar 🧀", "left"],
  ["J", "Ich krieg jetzt richtig Hunger", "right"],
  ["A", "Noch 3 Tage durchhalten", "left"],
  ["K", "Hat jemand Ersatz-Klopapier? Bei mir ist's knapp", "right"],
  ["A", "Klo ist in der Wohnung, hab ich genug", "left"],
  ["K", "Okay puh", "right"],
  ["L", "Kann jemand Sonnencreme mitbringen? Vergesse ich immer", "left"],
  ["A", "Gibt's bei mir im Bad", "right"],
  ["M", "Kommen wir ans Kind? Brauchen wir Wickelmöglichkeit?", "left"],
  ["A", "Bad ist frei, nimm was du brauchst", "right"],
  ["N", "Ich bring Fußball mit, okay fürs Grundstück?", "left"],
  ["A", "Klar, Platz ist genug", "right"],
  ["O", "Nochmal die Adresse?", "left"],
  ["A", "GARTENSTRASSE ZWÖLF", "right"],
  ["O", "Danke!", "left"],
  ["P", "Ich bring Blumen 🌻", "right"],
  ["A", "Wie süß 💛", "left"],
  ["Q", "Meine Nichte kommt auch", "right"],
  ["A", "Quentin wie viele jetzt??", "left"],
  ["Q", "6 von meiner Seite", "right"],
  ["A", "OH", "left"],
  ["B", "Ich bring noch Grill-Anzünder", "right"],
  ["F", "Hab ich schon", "left"],
  ["B", "Dann nehm ich den Feuerlöscher", "right"],
  ["F", "❓", "left"],
  ["B", "Immer dabei", "right"],
  ["C", "Bei der letzten Grillparty hat B fast das Nachbarhaus angezündet", "left"],
  ["A", "Haha stimmt", "right"],
  ["B", "Es war nur der Busch, es war NUR DER BUSCH", "left"],
  ["D", "Legendär 🔥", "right"],
  ["A", "Okay neue Frage: wer schneidet das Gemüse vor?", "left"],
  ["C", "Ich kann am Nachmittag helfen", "right"],
  ["L", "Ich auch", "left"],
  ["A", "Danke 🙏", "right"],
  ["E", "Brauch ich eine Kuchenform mitbringen oder nur fertigen Kuchen?", "left"],
  ["A", "Fertig reicht", "right"],
  ["F", "Update: 30 Bratwürste, 8 Veggie, Ketchup, Senf", "left"],
  ["A", "🫡", "right"],
  ["G", "Ich kann 2 Klappstühle bringen", "left"],
  ["A", "Super, hab nicht genug", "right"],
  ["H", "Brot-Update: 2 Schwarzbrot, 1 Baguette", "left"],
  ["I", "Chips-Update: 3 Sorten, paprika, salz, crème fraîche", "right"],
  ["A", "Ich liebe euch alle 💕", "left"],
  ["J", "Wie lang ist der Chat jetzt?", "right"],
  ["B", "250+", "left"],
  ["J", "Hilfe", "right"],
  ["A", "Deshalb: LISTE!", "left"],
  ["C", "Hör doch auf mit der Liste", "right"],
  ["A", "Ich SAGS ja", "left"],
  ["K", "Was soll ich nochmal bringen?", "right"],
  ["A", "Scroll hoch", "left"],
  ["K", "Das sind 200 Nachrichten", "right"],
  ["A", "EBEN", "left"],
  ["L", "Für Kinder was zum Basteln?", "right"],
  ["M", "Ich bring Straßenkreide", "left"],
  ["N", "Und Wasserbomben!", "right"],
  ["M", "Nein", "left"],
  ["N", "😢", "right"],
  ["O", "Noch ne Frage: Parken in der Straße?", "left"],
  ["A", "Ja, vor dem Haus ist Platz", "right"],
  ["P", "Soll ich mein Geschirr mitbringen falls Pappteller knapp?", "left"],
  ["A", "Gute Idee, danke", "right"],
  ["Q", "Nochmal Quentin: wir sind jetzt 4, mein Schwager kommt doch nicht", "left"],
  ["A", "Gott sei Dank", "right"],
  ["Q", "Was??", "left"],
  ["A", "Nichts", "right"],
  ["R", "Balu ist willkommen bestätigt?", "left"],
  ["A", "Ja, Balu ist willkommen", "right"],
  ["S", "Playlist-Vorschlag: 80er Pop?", "left"],
  ["T", "Kein 80er", "right"],
  ["S", "Jazz?", "left"],
  ["T", "Auch nicht", "right"],
  ["S", "Was denn dann?", "left"],
  ["T", "Überraschung.", "right"],
  ["A", "Tina übernimmt Musik. Endgültig.", "left"],
  ["S", "😤", "right"],
  ["B", "Wie viele sind wir insgesamt?", "left"],
  ["A", "Moment…", "right"],
  ["A", "Lass mich zählen", "left"],
  ["A", "1… 2… 3…", "right"],
  ["A", "…42? 45?", "left"],
  ["A", "KEINE AHNUNG", "right"],
  ["B", "Siehste", "left"],
  ["A", "DAS IST DAS CHAOS VON DEM ICH REDE", "right"],
  ["C", "Vielleicht gibt's ne App dafür?", "left"],
  ["A", "Ja GIBT es", "right"],
  ["A", "brings-mit.com", "left"],
  ["A", "Ich mach eine Liste. Jetzt.", "right"],
  ["B", "Endlich", "left"],
  ["C", "Danke", "right"],
  ["D", "Oh sorry ich bring nochmal Obst-Update", "left"],
  ["D", "Plus Trauben", "right"],
  ["A", "IN DER LISTE", "left"],
  ["E", "Hab die Liste gefunden, trag mich ein", "right"],
  ["A", "🎉", "left"],
  ["F", "Viel übersichtlicher", "right"],
  ["G", "Wer ist Klara eigentlich?", "left"],
  ["K", "Ich?? Klara. Seit Jahren dabei?", "right"],
  ["G", "Ach so sorry", "left"],
  ["H", "Hahaha Klassiker", "right"],
  ["I", "Brot noch nicht in der Liste?", "left"],
  ["A", "Trag dich doch selbst ein!", "right"],
  ["I", "Ach ja stimmt 😅", "left"],
  ["J", "Okay ich geh jetzt mal scrollen hoch und checke was ich verpasst habe", "right"],
  ["A", "J, BITTE GEH AUF DIE LISTE", "left"],
  ["J", "Welche Liste?", "right"],
  ["A", "brings-mit.com/sommerfest-bei-anna", "left"],
  ["J", "Okay teste kurz", "right"],
  ["J", "Ah cool", "left"],
  ["J", "Trage mich ein", "right"],
  ["A", "🙌", "left"],
  ["K", "Kinder dürfen baden?", "right"],
  ["A", "Nur der Pool hinten", "left"],
  ["K", "Badesachen also", "right"],
  ["A", "Badesachen, ja", "left"],
  ["L", "Ist der Pool geheizt?", "right"],
  ["A", "Nein, kalt", "left"],
  ["L", "Dann bring ich Neoprenanzug", "right"],
  ["A", "… okay Lukas", "left"],
  ["M", "Meine Kinder freuen sich schon", "right"],
  ["A", "Meine auch 💛", "left"],
  ["N", "Sollen wir Geschenke mitbringen? Ist das ein Geburtstag?", "right"],
  ["A", "Nein einfach so! Kein Geschenk nötig", "left"],
  ["N", "Ah okay, aber ich kenn dich — gut dass ich frage", "right"],
  ["A", "Haha", "left"],
  ["O", "Tagsüber oder abends mehr?", "right"],
  ["A", "17 bis open end", "left"],
  ["O", "Ich komm spät weil erst ein Termin", "right"],
  ["A", "Alles gut", "left"],
  ["P", "Kann ich die Nacht da bleiben falls spät?", "right"],
  ["A", "Gästezimmer frei", "left"],
  ["P", "Top 💫", "right"],
  ["Q", "Ich bring Schlafsäcke falls", "left"],
  ["A", "Brauchst nicht, Matratzen sind da", "right"],
  ["Q", "Okay", "left"],
  ["R", "Balus Futter muss ich mitbringen?", "right"],
  ["A", "Ja, bitte", "left"],
  ["R", "Und sein Kissen?", "right"],
  ["A", "Ja auch", "left"],
  ["R", "Und seinen Lieblingsball?", "right"],
  ["A", "R. Balu ist ein Hund.", "left"],
  ["R", "Ein sehr besonderer Hund.", "right"],
  ["S", "Tina?", "left"],
  ["T", "Ja?", "right"],
  ["S", "Bist du noch sauer wegen Musikbox?", "left"],
  ["T", "Nein", "right"],
  ["S", "Sicher?", "left"],
  ["T", "Sicher", "right"],
  ["S", "Okay 💙", "left"],
  ["T", "💙", "right"],
  ["A", "Awww", "left"],
  ["B", "Wir sind wie eine Serie", "right"],
  ["C", "Staffel 12, Folge 342: die Gartenparty", "left"],
  ["A", "😂", "right"],
  ["D", "Update: 3 Kilo Obst, ich glaub das reicht", "left"],
  ["A", "Glaub schon", "right"],
  ["E", "Apfelkuchen steht, 24 Portionen", "left"],
  ["A", "24 Portionen für uns??", "right"],
  ["E", "Dann bleibt was für den nächsten Tag", "left"],
  ["A", "Gute Strategie 🧠", "right"],
  ["F", "Fleisch ist besorgt und im Kühlschrank", "left"],
  ["A", "Danke 🙏", "right"],
  ["G", "Stühle stehen im Auto", "left"],
  ["H", "Brot ist gekauft", "right"],
  ["I", "Chips besorgt", "left"],
  ["J", "Ich bin dann auch endlich in der Liste", "right"],
  ["A", "Alle drin?", "left"],
  ["B", "Schaut gut aus!", "right"],
  ["A", "Genau deshalb", "left"],
  ["A", "LISTE > CHAT", "right"],
  ["B", "Zwei Meisterkuchen, drei Kartoffelsalat, Obst satt, Fleisch, Brot, Stühle", "left"],
  ["A", "Klingt perfekt 🎉", "right"],
  ["A", "Bis Samstag!", "left"],
  ["A", "Wer ist nicht in der Liste?", "right"],
  ["A", "..?", "left"],
  ["A", "Wer's nicht einträgt kommt nicht 😅", "right"],
  ["B", "Harte Frau", "left"],
  ["A", "Nein einfach effizient", "right"],
  ["C", "Organisiert heißt das", "left"],
  ["A", "Genau", "right"],
  ["E", "Ich hab die Liste zu den Nachbarn geschickt", "left"],
  ["A", "Jetzt kommen nochmal 50 Leute neu", "right"],
  ["E", "Nur Frau Meier", "left"],
  ["A", "Frau Meier 🧓 willkommen", "right"],
]);

const EN_MSGS: ChatMsg[] = mk([
  ["A", "Hey everyone! Who's bringing what?", "left"],
  ["B", "I can do salad 🥗", "right"],
  ["C", "Salad sounds good!", "left"],
  ["D", "Me too salad? Or something else?", "right"],
  ["B", "You do potato, I'll do pasta", "left"],
  ["E", "Cake from me", "right"],
  ["F", "I'll bring drinks", "left"],
  ["G", "Which drinks?", "right"],
  ["F", "Beer, wine, water?", "left"],
  ["G", "Non-alcoholic for the kids too please", "right"],
  ["F", "Ah right, the kids", "left"],
  ["H", "I'll bring bread", "right"],
  ["I", "I have grill tongs at home, I'll bring", "left"],
  ["J", "Who's bringing plates?", "right"],
  ["A", "Oh, plates? Didn't think of that", "left"],
  ["K", "I still have paper plates from last time", "right"],
  ["S", "I'll bring a speaker", "left"],
  ["T", "No, no speaker. Please.", "right"],
  ["S", "Yes.", "left"],
  ["T", "No.", "right"],
  ["S", "Compromise: quiet speaker", "left"],
  ["T", "Fine", "right"],
  ["A", "Folks, what about salad??", "left"],
  ["B", "I do pasta, C potato?", "right"],
  ["C", "I thought I was doing cake", "left"],
  ["E", "I thought I was doing cake!", "right"],
  ["A", "So two cakes?", "left"],
  ["C", "Okay I'll do potato salad", "right"],
  ["A", "Moment, I need to make a list 📝", "left"],
  ["B", "You always make lists", "right"],
  ["A", "Because otherwise it escalates like now", "left"],
  ["J", "Allergies? Anyone nut-allergic?", "right"],
  ["K", "I'm gluten-free, but I'll bring my own", "left"],
  ["L", "I'm vegan", "right"],
  ["O", "Where is this again?", "left"],
  ["A", "My garden, Gartenstraße 12", "right"],
  ["O", "Google Maps link?", "left"],
  ["A", "Search yourself 😏", "right"],
  ["Q", "Can I bring my colleague? He's new here", "left"],
  ["A", "Sure, welcome him", "right"],
  ["Q", "Cool he's bringing his girlfriend", "left"],
  ["A", "Even cooler 👋", "right"],
  ["Q", "She's pregnant, can I bring her brother too?", "left"],
  ["A", "Uh, yes? How many are we now?", "right"],
  ["Q", "Not sure", "left"],
  ["A", "Quentin 😑", "right"],
  ["R", "Are dogs coming?", "left"],
  ["A", "Yes, mine are here anyway", "right"],
  ["R", "My Balu is sometimes grumpy though", "left"],
  ["S", "I'll bring cooler and ice", "right"],
  ["T", "Good idea", "left"],
  ["S", "And the speaker.", "right"],
  ["T", "Sven no.", "left"],
  ["A", "You two are exhausting 😂", "right"],
  ["B", "Who's bringing napkins?", "left"],
  ["A", "I got it", "right"],
  ["C", "Ketchup & mustard?", "left"],
  ["F", "I'll get it from the butcher", "right"],
  ["K", "What am I bringing again?", "left"],
  ["A", "Scroll up", "right"],
  ["K", "That's 200 messages", "left"],
  ["A", "EXACTLY", "right"],
  ["A", "Folks focus", "left"],
  ["N", "Should we bring gifts? Is this a birthday?", "right"],
  ["A", "No just because! No gift needed", "left"],
  ["O", "Day or evening mostly?", "right"],
  ["A", "5pm to open end", "left"],
  ["R", "Balu's food I need to bring?", "right"],
  ["A", "Yes, please", "left"],
  ["R", "And his cushion?", "right"],
  ["A", "Yes that too", "left"],
  ["R", "And his favorite ball?", "right"],
  ["A", "R. Balu is a dog.", "left"],
  ["R", "A very special dog.", "right"],
  ["S", "Tina?", "left"],
  ["T", "Yes?", "right"],
  ["S", "Still mad about the speaker?", "left"],
  ["T", "No", "right"],
  ["A", "Awww", "left"],
  ["B", "We're like a TV show", "right"],
  ["C", "Season 12, Episode 342: The Garden Party", "left"],
  ["A", "😂", "right"],
  ["E", "Cake is standing, 24 portions", "left"],
  ["A", "24 portions for us??", "right"],
  ["E", "Then leftover for the next day", "left"],
  ["F", "Meat is bought and in the fridge", "right"],
  ["H", "Bread is bought", "left"],
  ["A", "LIST > CHAT", "right"],
  ["B", "Two cakes, three potato salads, fruit, meat, bread, chairs", "left"],
  ["A", "Sounds perfect 🎉", "right"],
  ["A", "See you Saturday!", "left"],
  ["A", "Who's not on the list yet?", "right"],
  ["A", "..?", "left"],
  ["A", "Whoever doesn't sign up isn't coming 😅", "right"],
  ["B", "Tough lady", "left"],
  ["A", "No just efficient", "right"],
  ["C", "Organized is the word", "left"],
  ["A", "Exactly", "right"],
]);

const ES_MSGS: ChatMsg[] = mk([
  ["A", "¡Hola a todos! ¿Quién trae qué?", "left"],
  ["B", "Yo puedo hacer ensalada 🥗", "right"],
  ["C", "¡La ensalada suena bien!", "left"],
  ["D", "¿Yo también ensalada? ¿O mejor otra cosa?", "right"],
  ["B", "Tú haz patata, yo pasta", "left"],
  ["E", "Tarta yo", "right"],
  ["F", "Yo traigo las bebidas", "left"],
  ["G", "¿Qué bebidas?", "right"],
  ["F", "¿Cerveza, vino, agua?", "left"],
  ["G", "Sin alcohol para los niños también por favor", "right"],
  ["H", "Yo traigo pan", "right"],
  ["J", "¿Alguien trae platos?", "right"],
  ["A", "Oh, ¿platos? No lo había pensado", "left"],
  ["S", "Yo traigo un altavoz", "left"],
  ["T", "No, altavoz no. Por favor.", "right"],
  ["S", "Sí.", "left"],
  ["T", "No.", "right"],
  ["S", "Compromiso: altavoz bajito", "left"],
  ["T", "Bueno", "right"],
  ["A", "Gente, ¿qué pasa con la ensalada??", "left"],
  ["B", "Yo pasta, ¿C patata?", "right"],
  ["C", "Yo pensaba que traía la tarta", "left"],
  ["E", "¡Yo pensaba que YO traía la tarta!", "right"],
  ["A", "¿Entonces dos tartas?", "left"],
  ["C", "Vale, yo hago ensaladilla", "right"],
  ["A", "Un momento, tengo que hacer una lista 📝", "left"],
  ["B", "Siempre haces listas", "right"],
  ["A", "Porque si no se lía como ahora", "left"],
  ["J", "¿Alergias? ¿Alguien alérgico a frutos secos?", "right"],
  ["K", "Yo sin gluten, pero traigo lo mío", "left"],
  ["L", "Yo vegano", "right"],
  ["O", "¿Dónde es otra vez?", "left"],
  ["A", "En mi jardín, Gartenstraße 12", "right"],
  ["Q", "¿Puedo traer a mi compañero? Es nuevo aquí", "left"],
  ["A", "Claro, bienvenido", "right"],
  ["Q", "Genial trae a su novia", "left"],
  ["A", "Aún más genial 👋", "right"],
  ["Q", "Ella está embarazada, ¿puedo traer a su hermano también?", "left"],
  ["A", "Eh, ¿sí? ¿Cuántos somos ya?", "right"],
  ["Q", "No estoy seguro", "left"],
  ["A", "Quentin 😑", "right"],
  ["R", "¿Vienen los perros?", "left"],
  ["A", "Sí, los míos están aquí igualmente", "right"],
  ["S", "Traigo nevera portátil y hielo", "left"],
  ["T", "Buena idea", "right"],
  ["S", "Y el altavoz.", "left"],
  ["T", "Sven no.", "right"],
  ["A", "Vosotros dos cansáis 😂", "left"],
  ["B", "¿Quién trae servilletas?", "right"],
  ["A", "Yo tengo", "left"],
  ["C", "¿Ketchup y mostaza?", "right"],
  ["F", "Lo cojo en el carnicero", "left"],
  ["K", "¿Qué traigo yo otra vez?", "right"],
  ["A", "Sube scrolleando", "left"],
  ["K", "Son 200 mensajes", "right"],
  ["A", "EXACTO", "left"],
  ["A", "Gente centrémonos", "right"],
  ["N", "¿Llevamos regalos? ¿Es un cumpleaños?", "left"],
  ["A", "¡No sin más! No hace falta regalo", "right"],
  ["O", "¿Día o noche más?", "left"],
  ["A", "17h hasta que aguantemos", "right"],
  ["R", "¿La comida de Balu la traigo yo?", "left"],
  ["A", "Sí, por favor", "right"],
  ["R", "¿Y su cojín?", "left"],
  ["A", "Sí también", "right"],
  ["R", "¿Y su pelota favorita?", "left"],
  ["A", "R. Balu es un perro.", "right"],
  ["R", "Un perro muy especial.", "left"],
  ["S", "¿Tina?", "right"],
  ["T", "¿Sí?", "left"],
  ["S", "¿Sigues enfadada por el altavoz?", "right"],
  ["T", "No", "left"],
  ["A", "Awww", "right"],
  ["B", "Somos como una serie", "left"],
  ["C", "Temporada 12, episodio 342: la fiesta", "right"],
  ["A", "😂", "left"],
  ["E", "Tarta lista, 24 porciones", "right"],
  ["A", "¿24 porciones para nosotros??", "left"],
  ["E", "Entonces sobra para el día siguiente", "right"],
  ["F", "Carne comprada y en la nevera", "left"],
  ["H", "Pan comprado", "right"],
  ["A", "¡LISTA > CHAT!", "left"],
  ["A", "¡Nos vemos el sábado!", "right"],
  ["A", "¿Quién no está todavía en la lista?", "left"],
  ["A", "..?", "right"],
  ["A", "Quien no se apunta no viene 😅", "left"],
  ["B", "Qué dura", "right"],
  ["A", "No solo eficiente", "left"],
  ["C", "Organizada es la palabra", "right"],
  ["A", "Exacto", "left"],
]);

const POOLS: Record<string, ChatMsg[]> = {
  de: DE_MSGS,
  en: EN_MSGS,
  es: ES_MSGS,
};

export function getChatPool(lang: string): ChatMsg[] {
  const key = lang.slice(0, 2);
  return POOLS[key] ?? POOLS.de;
}

/** Liefert ein zufälliges zusammenhängendes Fenster aus dem Pool.
 * Wenn Pool kleiner als size ist, wird der ganze Pool geliefert. */
export function getRandomWindow(pool: ChatMsg[], size: number): ChatMsg[] {
  if (pool.length <= size) return pool.slice();
  const maxStart = pool.length - size;
  const start = Math.floor(Math.random() * (maxStart + 1));
  return pool.slice(start, start + size);
}
